import { useMemo } from 'react';
import type { DrawerProps } from '@mui/material';
import { VyDrawer, VyThemeProvider } from '@vy/ui-design-system';
import {
  isPerUnitUsageProductOnly,
  isTieredSubscriptionUsageProductOnly,
  isTiersUsageProductOnly,
} from '@vy/services';
import type { UsageProductInput } from '@vy/types';
import { DEFAULT_CURRENCY } from '@vy/types';
import { raise, vySerialize } from '@vy/utils';
import { ChargeProvider } from '../charge-context';
import { useModifyPanel } from '../modify-panel-shared/hooks/use-modify-panel';
import { PerUnitVariant, TieredSubscriptionVariant, TiersVariant } from './variants';

export type UsageFeesModifyPanelProps = Pick<DrawerProps, 'open' | 'onClose'> & {
  usageProduct: UsageProductInput;
};

export function UsageFeesModifyPanel({ usageProduct, ...props }: UsageFeesModifyPanelProps) {
  const Variant = useMemo(() => pickVariant(usageProduct), [usageProduct]);

  const { productIndex, drawerTitle, handleClose, handleCancel, handleSave } = useModifyPanel({
    arrayName: 'usageProducts',
    product: usageProduct,
    open: props.open,
    onClose: props.onClose,
  });

  return (
    <VyThemeProvider>
      <VyDrawer
        open={Boolean(props.open)}
        onClose={handleClose}
        width="50%"
        title={drawerTitle}
        subtitle="Configure pricing, schedule, and metering settings for this usage product."
        onCancel={handleCancel}
        onSave={handleSave}
        data-vy="modify-usage-product-drawer"
      >
        <ChargeProvider
          value={{
            item: usageProduct,
            itemIndex: productIndex,
            editable: true,
            currency: DEFAULT_CURRENCY,
          }}
        >
          <Variant />
        </ChargeProvider>
      </VyDrawer>
    </VyThemeProvider>
  );
}

function pickVariant(product: UsageProductInput): React.FC {
  if (isTiersUsageProductOnly(product)) return TiersVariant;
  if (isPerUnitUsageProductOnly(product)) return PerUnitVariant;
  if (isTieredSubscriptionUsageProductOnly(product)) return TieredSubscriptionVariant;
  return raise(`unknown usage product type ${vySerialize(product, null, 2)}`);
}
